import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AlertTriangle, CopyPlus, MapPin, MapPinned, Plus } from 'lucide-react';
import api from '../../../lib/api';
import Button from '../../../components/Button';
import Chip from '../../../components/Chip';
import AddressAutocomplete from '../../../components/AddressAutocomplete';
import HelpTrigger from '../../../help/components/HelpTrigger';
import {
  StandardTable,
  StandardTableBody,
  StandardTableCell,
  StandardTableHeader,
  StandardTableHeaderCell,
  StandardTableRow,
  EmptyTableState,
  LoadingTableState,
} from '../../../components/StandardTable';
import AdminModal from '../../components/AdminModal';
import AdminListToolbar from '../../components/AdminListToolbar';
import AdminStatCard from '../../components/AdminStatCard';
import ListPager from '../../components/ListPager';
import MultiSelectFilter, { type MultiSelectOption } from '../../components/MultiSelectFilter';
import { AdminListResult, useAdminList } from '../../hooks/useAdminList';
import { LOCATION_TYPES, locationTypeShort } from '../../../constants/votingLocations';
import ElectionLocationsSummary from './ElectionLocationsSummary';
import type { ElectionCycle } from './CyclesView';

export interface VotingPlaceRow {
  id: string;
  display_name: string;
  display_name_source: 'file' | 'address' | 'admin';
  county_name: string;
  county_district_id: string | null;
  address_line1: string;
  address_line2: string | null;
  city: string | null;
  zip: string | null;
  latitude: number | null;
  longitude: number | null;
  manual_fields: string[];
  active: boolean;
  location_types: string[];
  availability_count: number;
}

const parsePlaces = (raw: unknown): AdminListResult<VotingPlaceRow> => {
  const data = raw as { data?: VotingPlaceRow[]; total?: number; limit?: number; offset?: number };
  const total = data.total ?? 0;
  const limit = data.limit ?? 0;
  const offset = data.offset ?? 0;
  return { rows: data.data ?? [], total, hasMore: offset + limit < total };
};

/** Counties come from the seeded co_county districts, never free text. */
const fetchCounties = async (term: string): Promise<MultiSelectOption[]> => {
  const response = await api.get('/reference/electoral-districts', {
    params: { state_code: 'CO', scope: 'co_county' },
  });
  const districts = (response.data.data?.districts ?? []) as Array<{ id: string; display_label: string }>;
  const needle = term.trim().toLowerCase();
  return districts
    .filter((district) => !needle || district.display_label.toLowerCase().includes(needle))
    .map((district) => ({ id: district.id, label: district.display_label }));
};

const filterSelectClasses =
  'h-9 rounded-lg border border-border-light bg-white px-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
const inputClasses =
  'w-full rounded-lg border border-border-light bg-white px-3 py-2 text-sm text-text-primary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-primary';
const labelClasses = 'mb-1 block text-sm font-medium text-text-secondary dark:text-text-dark-secondary';
const toggleClasses =
  'flex h-9 items-center gap-1.5 rounded-lg border border-border-light bg-white px-2.5 text-sm text-text-secondary dark:border-border-dark dark:bg-background-dark-secondary dark:text-text-dark-secondary';

const COLUMN_COUNT = 6;

const EMPTY_FORM = {
  display_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  zip: '',
  county_district_id: '',
  county_name: '',
};

const apiError = (error: any, fallback: string) =>
  error?.response?.data?.error ?? error?.response?.data?.message ?? fallback;

/**
 * Voting Locations — the durable places registry, and the QA view for whatever
 * the last workbook import produced (VOTING_LOCATIONS_ADMIN_TOOL_PRD.md §5.2).
 *
 * Selecting a cycle turns the list into that election's view and reveals its
 * import summary above the table, so "did the import work?" and "fix this row"
 * are the same screen rather than two.
 */
const LocationsView: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [countyIds, setCountyIds] = useState<string[]>([]);
  const [type, setType] = useState('');
  const [cycleId, setCycleId] = useState('');
  const [needsAttention, setNeedsAttention] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySourceId, setCopySourceId] = useState('');
  // Destination is held here, not read off the list filter. Inheriting it made
  // the only dropdown in the dialog the *source*, with the destination implied
  // by a filter behind it — which read as "copy from 2026" to everyone who saw it.
  const [copyTargetId, setCopyTargetId] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);

  const cycles = useQuery({
    queryKey: ['admin-elections', 'cycles'],
    queryFn: async () => {
      const response = await api.get('/election-calendars');
      return (response.data.data ?? []) as ElectionCycle[];
    },
  });
  const cyclesNewestFirst = useMemo(
    () => [...(cycles.data ?? [])].sort((a, b) => b.election_year - a.election_year),
    [cycles.data]
  );

  const counties = useQuery({
    queryKey: ['admin-elections', 'counties'],
    queryFn: () => fetchCounties(''),
    staleTime: 10 * 60 * 1000,
    enabled: addOpen,
  });

  const stats = useQuery({
    queryKey: ['admin-voting-locations', 'stats', cycleId],
    queryFn: async () => {
      const response = await api.get('/admin/voting-locations/stats', {
        params: { electionCalendarId: cycleId || undefined },
      });
      return response.data.data as {
        active_places: number;
        inactive_places: number;
        counties: number;
        needs_attention: number;
        availability_rows: number;
      };
    },
  });

  const list = useAdminList<VotingPlaceRow>({
    queryKey: 'voting-locations',
    endpoint: '/admin/voting-locations',
    parse: parsePlaces,
    filters: {
      countyDistrictId: countyIds.length > 0 ? countyIds : undefined,
      type: type || undefined,
      electionCalendarId: cycleId || undefined,
      needsAttention: needsAttention ? 'true' : undefined,
      includeInactive: includeInactive ? 'true' : undefined,
    },
    initialSort: { key: 'display_name', direction: 'asc' },
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-list', 'voting-locations'] });
    queryClient.invalidateQueries({ queryKey: ['admin-voting-locations'] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/voting-locations', {
        display_name: form.display_name.trim(),
        address_line1: form.address_line1.trim(),
        address_line2: form.address_line2.trim() || undefined,
        city: form.city.trim() || undefined,
        zip: form.zip.trim() || undefined,
        county_district_id: form.county_district_id || undefined,
        county_name: form.county_name.trim(),
      }),
    onSuccess: (response) => {
      toast.success('Voting place added');
      setAddOpen(false);
      invalidate();
      const created = response.data.data as VotingPlaceRow | undefined;
      if (created?.id) navigate(`/admin-next/elections/locations/${created.id}`);
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to add the voting place')),
  });

  const coverage = useQuery({
    queryKey: ['admin-voting-locations', 'geocode-coverage'],
    queryFn: async () => {
      const response = await api.get('/admin/voting-locations/geocode/coverage');
      return response.data.data as {
        active_places: number;
        geocoded: number;
        pending: number;
        failed: number;
        imprecise: number;
      };
    },
  });

  /**
   * Geocoding runs as a loop of small batches, not one long request.
   *
   * ~500 addresses take minutes and the API client times out at 10 seconds, so a
   * single call can never finish. Each batch is resumable — the server picks
   * whatever still has no coordinate — which also means closing this page mid-run
   * only costs the batch in flight.
   */
  const [geocodeProgress, setGeocodeProgress] = useState<{ done: number; failed: number } | null>(null);

  const geocodeMutation = useMutation({
    mutationFn: async () => {
      const totals = { done: 0, failed: 0 };
      setGeocodeProgress(totals);

      // Bounded so a server that stops making progress can't spin forever.
      for (let batch = 0; batch < 60; batch += 1) {
        const response = await api.post(
          '/admin/voting-locations/geocode',
          { retryFailed: false },
          { timeout: 60000 }
        );
        const { considered, geocoded, failed } = response.data.data as {
          considered: number;
          geocoded: number;
          failed: number;
        };

        if (considered === 0) break;

        totals.done += geocoded;
        totals.failed += failed;
        setGeocodeProgress({ ...totals });
        // Refresh the banner as it goes, so progress is visible rather than implied.
        queryClient.invalidateQueries({ queryKey: ['admin-voting-locations', 'geocode-coverage'] });
      }

      return totals;
    },
    onSuccess: (totals) => {
      if (totals.done === 0 && totals.failed === 0) {
        toast.success('Every active place already has coordinates');
      } else {
        toast.success(
          `Mapped ${totals.done} place${totals.done === 1 ? '' : 's'}` +
          (totals.failed ? ` — ${totals.failed} address${totals.failed === 1 ? '' : 'es'} could not be found` : '')
        );
      }
      setGeocodeProgress(null);
      invalidate();
    },
    onError: (error: any) => {
      // Whatever finished before the failure is already saved; the next run resumes.
      setGeocodeProgress(null);
      invalidate();
      toast.error(apiError(error, 'Geocoding stopped — press again to resume where it left off'));
    },
  });

  const copyMutation = useMutation({
    mutationFn: () =>
      api.post('/admin/voting-locations/copy-to-cycle', {
        sourceElectionCalendarId: copySourceId,
        targetElectionCalendarId: copyTargetId,
      }),
    onSuccess: (response) => {
      const { copied } = response.data.data as { copied: number };
      toast.success(`Copied ${copied} location${copied === 1 ? '' : 's'} as projected placeholders`);
      setCopyOpen(false);
      // Show the cycle that was just populated, rather than leaving the admin on
      // a filter that may not be the one they copied into.
      setCycleId(copyTargetId);
      invalidate();
    },
    onError: (error: any) => toast.error(apiError(error, 'Failed to copy locations')),
  });

  /** Pre-fills the destination from the current filter, but it stays editable. */
  const openCopy = () => {
    setCopySourceId('');
    setCopyTargetId(cycleId);
    setCopyOpen(true);
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setAddOpen(true);
  };

  /** The county select carries the label; the text column stores the bare name. */
  const onCountyChange = (districtId: string) => {
    const chosen = (counties.data ?? []).find((option) => option.id === districtId);
    setForm((current) => ({
      ...current,
      county_district_id: districtId,
      county_name: chosen ? chosen.label.replace(/\s+County$/i, '') : current.county_name,
    }));
  };

  const saveDisabled =
    form.display_name.trim() === '' ||
    form.address_line1.trim() === '' ||
    form.county_name.trim() === '';

  const selectedCycle = cyclesNewestFirst.find((cycle) => cycle.id === cycleId);

  /**
   * An empty table has three quite different causes, and pointing at the wrong
   * remedy sends someone to the Data Import tool when the fix is one button away.
   */
  const emptyMessage = (() => {
    if (list.search) return `No voting places match "${list.search}"`;

    // Places exist, but none of them do anything in the selected cycle — so the
    // registry is fine and the cycle is what needs filling.
    const hasPlacesOverall = (stats.data?.active_places ?? 0) > 0;
    if (cycleId && hasPlacesOverall) {
      return `No locations in ${selectedCycle?.name ?? 'this cycle'} yet. Use "Copy into cycle" to bring them forward from another election, or import the state's file for it.`;
    }

    if (needsAttention || countyIds.length > 0 || type) {
      return 'No voting places match these filters.';
    }

    return 'No voting places found. Locations arrive through the Data Import tool.';
  })();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1.5">
        <h2 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
          Voting Locations
        </h2>
        <HelpTrigger contentKey="voting-locations-maintenance" size="sm" />
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <AdminStatCard label="Active places" value={stats.data?.active_places} icon={MapPin} loading={stats.isLoading} />
        <AdminStatCard label="Counties" value={stats.data?.counties} icon={MapPin} loading={stats.isLoading} />
        <AdminStatCard
          label={cycleId ? 'Locations this cycle' : 'Location records'}
          value={stats.data?.availability_rows}
          icon={MapPin}
          loading={stats.isLoading}
        />
        <AdminStatCard label="Needs attention" value={stats.data?.needs_attention} icon={AlertTriangle} loading={stats.isLoading} />
      </div>

      {/* Distance search in the citizen app only works for places that have been
          mapped, so the gap is worth stating plainly rather than leaving it to a
          column of dashes. */}
      {coverage.data && coverage.data.pending + coverage.data.failed > 0 && (
        <div className="card flex flex-wrap items-center gap-3 p-3">
          <MapPinned className="h-5 w-5 shrink-0 text-warning-600 dark:text-warning-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            <span className="font-medium text-text-primary dark:text-text-dark-primary">
              {coverage.data.geocoded} of {coverage.data.active_places} places mapped.
            </span>{' '}
            {coverage.data.pending > 0 && `${coverage.data.pending} still need coordinates. `}
            {coverage.data.failed > 0 && `${coverage.data.failed} could not be found. `}
            Citizens can't search by distance from an unmapped place.
          </p>
          <Button
            variant="outline"
            size="sm"
            icon={MapPinned}
            disabled={geocodeMutation.isLoading || coverage.data.pending === 0}
            onClick={() => geocodeMutation.mutate()}
          >
            {geocodeMutation.isLoading
              ? geocodeProgress
                ? `Mapping… ${geocodeProgress.done} done`
                : 'Mapping…'
              : `Map ${coverage.data.pending} places`}
          </Button>
        </div>
      )}

      <AdminListToolbar
        search={list.search}
        onSearch={list.setSearch}
        searchPlaceholder="Search by name, address, or city…"
        searchAriaLabel="Search voting places"
        actions={
          <>
            {/* No longer gated on the list filter: the dialog asks for both
                cycles outright, so there is nothing to set up first. */}
            <Button variant="outline" size="sm" icon={CopyPlus} onClick={openCopy}>
              Copy into cycle
            </Button>
            <Button variant="primary" size="sm" icon={Plus} onClick={openAdd}>
              Add Place
            </Button>
          </>
        }
      >
        <select
          value={cycleId}
          onChange={(event) => setCycleId(event.target.value)}
          aria-label="Filter by election cycle"
          className={filterSelectClasses}
        >
          <option value="">All cycles</option>
          {cyclesNewestFirst.map((cycle) => (
            <option key={cycle.id} value={cycle.id}>
              {cycle.name || `${cycle.election_year} ${cycle.state} ${cycle.election_type}`}
            </option>
          ))}
        </select>

        <MultiSelectFilter
          label="Counties"
          fetchOptions={fetchCounties}
          selectedIds={countyIds}
          onChange={setCountyIds}
        />

        <select
          value={type}
          onChange={(event) => setType(event.target.value)}
          aria-label="Filter by location type"
          className={filterSelectClasses}
        >
          <option value="">All types</option>
          {LOCATION_TYPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <label className={toggleClasses}>
          <input
            type="checkbox"
            checked={needsAttention}
            onChange={(event) => setNeedsAttention(event.target.checked)}
          />
          Needs attention
        </label>

        <label className={toggleClasses}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          Include deactivated
        </label>
      </AdminListToolbar>

      {cycleId && <ElectionLocationsSummary electionCalendarId={cycleId} cycleName={selectedCycle?.name ?? null} />}

      {list.isError ? (
        <div className="card flex items-center gap-3 border-l-4 border-error-500 p-4">
          <AlertTriangle className="h-5 w-5 shrink-0 text-error-600 dark:text-error-400" />
          <p className="flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
            Couldn't load voting places. Please try again.
          </p>
          <Button variant="outline" size="sm" onClick={() => list.refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <StandardTable horizontalScroll loading={list.isFetching && !list.isLoading}>
          <StandardTableHeader>
            <StandardTableRow>
              <StandardTableHeaderCell
                sortable sortKey="display_name" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort}
              >
                Name
              </StandardTableHeaderCell>
              <StandardTableHeaderCell
                sortable sortKey="city" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort}
              >
                Address
              </StandardTableHeaderCell>
              <StandardTableHeaderCell
                sortable sortKey="county_name" currentSortKey={list.sortKey}
                sortDirection={list.sortDirection} onSort={list.handleSort} columnType="fit"
              >
                County
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">
                {cycleId ? 'Roles this cycle' : 'Roles'}
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit" responsiveHide="lg">
                Coordinates
              </StandardTableHeaderCell>
              <StandardTableHeaderCell columnType="fit">Flags</StandardTableHeaderCell>
            </StandardTableRow>
          </StandardTableHeader>
          <StandardTableBody>
            {list.isLoading ? (
              <LoadingTableState colSpan={COLUMN_COUNT} />
            ) : list.rows.length === 0 ? (
              <EmptyTableState
                colSpan={COLUMN_COUNT}
                message={emptyMessage}
                icon={<MapPin className="h-8 w-8" />}
              />
            ) : (
              list.rows.map((row) => (
                <StandardTableRow
                  key={row.id}
                  onClick={() => navigate(`/admin-next/elections/locations/${row.id}`)}
                >
                  <StandardTableCell>
                    <span className="line-clamp-1 font-medium text-text-primary dark:text-text-dark-primary">
                      {row.display_name}
                    </span>
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="flex flex-col">
                      <span className="line-clamp-1 text-text-primary dark:text-text-dark-primary">
                        {row.address_line1}
                      </span>
                      {row.city && (
                        <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                          {row.city}{row.zip ? ` ${row.zip}` : ''}
                        </span>
                      )}
                    </div>
                  </StandardTableCell>
                  <StandardTableCell>{row.county_name}</StandardTableCell>
                  <StandardTableCell>
                    <div className="flex flex-wrap gap-1">
                      {row.location_types.length === 0 ? (
                        <span className="text-text-tertiary dark:text-text-dark-tertiary">—</span>
                      ) : (
                        row.location_types.map((locationType) => (
                          <Chip key={locationType} label={locationTypeShort(locationType)} variant="neutral" size="sm" />
                        ))
                      )}
                    </div>
                  </StandardTableCell>
                  <StandardTableCell responsiveHide="lg">
                    {row.latitude != null && row.longitude != null ? '✓' : '—'}
                  </StandardTableCell>
                  <StandardTableCell>
                    <div className="flex flex-wrap gap-1">
                      {!row.active && <Chip label="Deactivated" variant="neutral" size="sm" />}
                      {row.manual_fields?.length > 0 && <Chip label="Edited" variant="success" size="sm" />}
                      {row.display_name_source === 'address' && (
                        <Chip label="Name from address" variant="warning" size="sm" />
                      )}
                      {row.county_district_id === null && (
                        <Chip label="County unmapped" variant="warning" size="sm" />
                      )}
                    </div>
                  </StandardTableCell>
                </StandardTableRow>
              ))
            )}
          </StandardTableBody>
        </StandardTable>
      )}

      <ListPager
        offset={list.offset}
        limit={list.limit}
        total={list.total}
        page={list.page}
        pageCount={list.pageCount}
        hasPrev={list.hasPrev}
        hasNext={list.hasNext}
        onPrev={list.prevPage}
        onNext={list.nextPage}
        noun="places"
      />

      <AdminModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add voting place"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={saveDisabled || createMutation.isLoading}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isLoading ? 'Adding…' : 'Add place'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            For a site the state's file doesn't list. Everything you enter here is kept through
            future imports.
          </p>
          <div>
            <label className={labelClasses} htmlFor="place-name">Name</label>
            <input
              id="place-name"
              className={inputClasses}
              value={form.display_name}
              onChange={(event) => setForm({ ...form, display_name: event.target.value })}
            />
          </div>
          <div>
            <label className={labelClasses} htmlFor="place-address">Street address</label>
            {/* Google Places suggestions; picking one fills city and ZIP too. */}
            <AddressAutocomplete
              id="place-address"
              inputClassName={inputClasses}
              showIcon={false}
              placeholder=""
              value={form.address_line1}
              onChange={(value) => setForm((current) => ({ ...current, address_line1: value }))}
              onSelect={(parts) => setForm((current) => ({
                ...current,
                city: parts.city || current.city,
                zip: parts.zip || current.zip,
              }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses} htmlFor="place-city">City</label>
              <input
                id="place-city"
                className={inputClasses}
                value={form.city}
                onChange={(event) => setForm({ ...form, city: event.target.value })}
              />
            </div>
            <div>
              <label className={labelClasses} htmlFor="place-zip">ZIP</label>
              <input
                id="place-zip"
                className={inputClasses}
                value={form.zip}
                onChange={(event) => setForm({ ...form, zip: event.target.value })}
              />
            </div>
          </div>
          <div>
            <label className={labelClasses} htmlFor="place-county">County</label>
            <select
              id="place-county"
              className={inputClasses}
              value={form.county_district_id}
              onChange={(event) => onCountyChange(event.target.value)}
            >
              <option value="">Select a county…</option>
              {(counties.data ?? []).map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </AdminModal>

      <AdminModal
        isOpen={copyOpen}
        onClose={() => setCopyOpen(false)}
        title="Copy locations into a cycle"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setCopyOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={!copySourceId || !copyTargetId || copyMutation.isLoading}
              onClick={() => copyMutation.mutate()}
            >
              {copyMutation.isLoading ? 'Copying…' : 'Copy locations'}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            Copies every location from one cycle into another as a <strong>placeholder</strong>.
            Dates are not copied — last cycle's voting window doesn't apply to the new election, and
            showing the wrong dates is worse than showing none.
          </p>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
            When the state's real file is imported for the destination cycle, these placeholders are
            replaced automatically.
          </p>

          {/* Both cycles are named outright. The direction of the copy is the one
              thing that must not be guessable from context. */}
          <div>
            <label className={labelClasses} htmlFor="copy-source">Copy from</label>
            <select
              id="copy-source"
              className={inputClasses}
              value={copySourceId}
              onChange={(event) => setCopySourceId(event.target.value)}
            >
              <option value="">Select the cycle to copy from…</option>
              {cyclesNewestFirst
                .filter((cycle) => cycle.id !== copyTargetId)
                .map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name || `${cycle.election_year} ${cycle.state} ${cycle.election_type}`}
                  </option>
                ))}
            </select>
          </div>

          <div>
            <label className={labelClasses} htmlFor="copy-target">Copy into</label>
            <select
              id="copy-target"
              className={inputClasses}
              value={copyTargetId}
              onChange={(event) => setCopyTargetId(event.target.value)}
            >
              <option value="">Select the cycle to copy into…</option>
              {cyclesNewestFirst
                .filter((cycle) => cycle.id !== copySourceId)
                .map((cycle) => (
                  <option key={cycle.id} value={cycle.id}>
                    {cycle.name || `${cycle.election_year} ${cycle.state} ${cycle.election_type}`}
                  </option>
                ))}
            </select>
          </div>

          {copySourceId && copyTargetId && (
            <p className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
              {cyclesNewestFirst.find((c) => c.id === copySourceId)?.name}
              {' → '}
              {cyclesNewestFirst.find((c) => c.id === copyTargetId)?.name}
            </p>
          )}
        </div>
      </AdminModal>
    </div>
  );
};

export default LocationsView;
